import { isPast } from 'date-fns';

import { UserData, CommonInit } from '../types/user.type';

/**
 * Utility functions for checking user subscription and plan status.
 * These functions provide consistent logic for determining user status across the application.
 */

/**
 * Checks if application is in boxed mode (SaaS for Sibur)
 * @param commonContext - CommonInit object from API
 * @returns true if boxed mode is enabled
 */
export const isBoxedMode = (commonContext: CommonInit | undefined | null): boolean => {
  return !!commonContext?.boxed;
};

/**
 * Checks if user has a trial plan
 * @param userData - User data object
 * @returns true if user has trial plan
 */
export const isTrialUser = (userData: UserData | undefined | null): boolean => {
  return !!userData?.product_balance?.product?.is_trial;
};

/**
 * Checks if user has an active trial plan (both is_active and is_trial must be true)
 * Used in chat-input and chat-footer
 * @param userData - User data object
 * @returns true if user has active trial plan
 */
export const isActiveTrialUser = (userData: UserData | undefined | null): boolean => {
  return !!(userData?.product_balance?.is_active && userData?.product_balance?.product?.is_trial);
};

/**
 * Checks if user has a subscription plan (not PayG)
 * @param userData - User data object
 * @returns true if user has subscription plan
 */
export const isSubscriptionPlan = (userData: UserData | undefined | null): boolean => {
  return userData?.product_balance?.type === 'subscription';
};

/**
 * Checks if user has a Pay-as-you-go plan
 * @param userData - User data object
 * @returns true if user has PayG plan
 */
export const isPayGPlan = (userData: UserData | undefined | null): boolean => {
  return userData?.product_balance?.type === 'presentation';
};

/**
 * Checks if user's plan is active
 * @param userData - User data object
 * @returns true if plan is active
 */
export const isPlanActive = (userData: UserData | undefined | null): boolean => {
  return !!userData?.product_balance?.is_active;
};

/**
 * Checks if user's subscription is active (separate flag from product_balance.is_active)
 * @param userData - User data object
 * @returns true if subscription is active
 */
export const isSubscriptionActive = (userData: UserData | undefined | null): boolean => {
  return !!userData?.subscription_active;
};

/**
 * Gets expiration date as Date object
 * @param userData - User data object
 * @returns Date object or null if invalid
 */
export const getExpirationDate = (userData: UserData | undefined | null): Date | null => {
  const expirationDate = userData?.product_balance?.expire;
  if (!expirationDate) return null;

  const expDateObj = new Date(expirationDate);
  return expDateObj instanceof Date && !isNaN(expDateObj.getTime()) ? expDateObj : null;
};

/**
 * Checks if expiration date is valid and in the past
 * @param userData - User data object
 * @returns true if expiration date exists and is in the past
 */
export const isExpirationDatePast = (userData: UserData | undefined | null): boolean => {
  const expDate = getExpirationDate(userData);
  return expDate !== null && isPast(expDate);
};

/**
 * Checks if trial plan is expired
 * @param userData - User data object
 * @returns true if user has trial plan and it's expired
 */
export const isExpiredTrial = (userData: UserData | undefined | null): boolean => {
  if (!isTrialUser(userData)) return false;
  return isExpirationDatePast(userData);
};

/**
 * Checks if expiration date is in the past (without checking if it's trial)
 * Used in actions-panel.block.tsx - checks date for any plan type
 * @param userData - User data object
 * @returns true if expiration date exists and is in the past
 */
export const isExpirationDatePastSimple = (userData: UserData | undefined | null): boolean => {
  const expirationDate = userData?.product_balance?.expire;
  if (!expirationDate) return false;
  const expDateObj = new Date(expirationDate);
  return expDateObj instanceof Date && !isNaN(expDateObj.getTime()) && isPast(expDateObj);
};

/**
 * Checks if subscription plan (not trial) is expired
 * @param userData - User data object
 * @returns true if user has subscription plan (not trial) and it's expired
 */
export const isExpiredSubscription = (userData: UserData | undefined | null): boolean => {
  if (!isSubscriptionPlan(userData) || isTrialUser(userData)) return false;
  return !isPlanActive(userData) || isExpirationDatePast(userData);
};

/**
 * Checks if PayG plan is expired (no remaining downloads)
 * @param userData - User data object
 * @returns true if user has PayG plan and no remaining downloads
 */
export const isExpiredPayGPlan = (userData: UserData | undefined | null): boolean => {
  if (!isPayGPlan(userData)) return false;
  const remainingDownloads = userData?.product_balance?.count ?? 0;
  return remainingDownloads === 0;
};

/**
 * Checks if any plan is expired (trial, subscription, or PayG)
 * Uses logic from payments-dialog.tsx: for subscription checks only date, for PayG checks downloads
 * @param userData - User data object
 * @returns true if plan is expired
 */
export const isExpired = (userData: UserData | undefined | null): boolean => {
  if (isSubscriptionPlan(userData)) {
    // For subscription: check only expiration date (as in payments-dialog.tsx)
    return isExpirationDatePast(userData);
  }
  if (isPayGPlan(userData)) {
    return isExpiredPayGPlan(userData);
  }
  return false;
};

/**
 * Checks if subscription plan is expired by date only (without checking is_active)
 * Used in payments-dialog.tsx logic
 * @param userData - User data object
 * @returns true if subscription plan and expiration date is in the past
 */
export const isExpiredSubscriptionByDate = (userData: UserData | undefined | null): boolean => {
  if (!isSubscriptionPlan(userData)) return false;
  return isExpirationDatePast(userData);
};

/**
 * Checks if user has any active plan (subscription or PayG with downloads)
 * @param userData - User data object
 * @returns true if user has active plan
 */
export const hasActivePlan = (userData: UserData | undefined | null): boolean => {
  if (!userData?.product_balance) return false;
  return isPlanActive(userData) && !isExpired(userData);
};

/**
 * Checks if user can download (has active plan or trial with remaining downloads)
 * @param userData - User data object
 * @returns true if user can download
 */
export const canDownload = (userData: UserData | undefined | null): boolean => {
  if (!userData?.product_balance) return false;

  // Active subscription (not trial)
  if (isSubscriptionPlan(userData) && !isTrialUser(userData) && hasActivePlan(userData)) {
    return true;
  }

  // Active PayG with downloads
  if (isPayGPlan(userData) && !isExpiredPayGPlan(userData)) {
    return true;
  }

  // Trial users cannot download
  return false;
};

/**
 * Checks if user has Pro plan (active subscription, not trial)
 * Used in pricing.page.tsx
 * @param userData - User data object
 * @returns true if user has active subscription plan (not trial)
 */
export const isProPlan = (userData: UserData | undefined | null): boolean => {
  return !isTrialUser(userData) && isPlanActive(userData) && isSubscriptionPlan(userData);
};

/**
 * Gets maximum allowed slides count for user
 * Trial and expired users: 10 slides
 * Active paid users: 50 slides
 * @param userData - User data object
 * @returns Maximum allowed slides count
 */
export const getMaxSlidesCount = (userData: UserData | undefined | null): number => {
  // Trial users (active or expired) - 10 slides
  if (isTrialUser(userData)) {
    return 10;
  }

  // Expired subscription users - 10 slides (same as trial)
  if (isExpiredSubscription(userData)) {
    return 10;
  }

  // Active paid users - 50 slides
  return 50;
};

/**
 * Checks if user should have trial-like restrictions (trial or expired subscription)
 * Used for determining UI behavior and limits
 * @param userData - User data object
 * @returns true if user has trial-like restrictions
 */
export const hasTrialLikeRestrictions = (userData: UserData | undefined | null): boolean => {
  return isTrialUser(userData) || isExpiredSubscription(userData);
};

/**
 * Gets total AI tokens count (included + purchased)
 * @param userData - User data object
 * @returns Total tokens count or undefined if product_balance is not available
 */
export const getTotalAiTokensCount = (
  userData: UserData | undefined | null,
): number | undefined => {
  if (!userData?.product_balance) return undefined;
  const { included_ai_token_count = 0, purchased_ai_token_count = 0 } = userData.product_balance;
  return included_ai_token_count + purchased_ai_token_count;
};
